import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Switch,
  Select,
  message,
  Popconfirm,
  Tag
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { exportToExcel } from '../../utils/excelExport';
import { getAnimalNameWithEmoji } from '../../utils/animalEmojis';
import adminBreedService from '../../services/adminBreedService';
import petService from '../../services/petService';
import BackButton from '../../components/BackButton';

const { Option } = Select;

const BreedList = () => {
  const { t, i18n } = useTranslation();
  const [breeds, setBreeds] = useState([]);
  const [animalCategories, setAnimalCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingBreed, setEditingBreed] = useState(null);
  const [filterCategory, setFilterCategory] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchAnimalCategories();
    fetchBreeds();
  }, []);

  const fetchAnimalCategories = async () => {
    const result = await petService.fetchAnimalCategories();
    if (result.success) {
      setAnimalCategories(result.data.results || result.data);
    }
  };

  const fetchBreeds = async (categoryId = null) => {
    setLoading(true);
    const params = categoryId ? { animal_category: categoryId } : {};
    const result = await adminBreedService.getBreeds(params);
    if (result.success) {
      setBreeds(result.data);
    } else {
      message.error(result.error);
    }
    setLoading(false);
  };

  const handleFilterChange = (value) => {
    setFilterCategory(value);
    fetchBreeds(value);
  };

  const handleAdd = () => {
    setEditingBreed(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true });
    setModalVisible(true);
  };

  const handleEdit = (breed) => {
    setEditingBreed(breed);
    form.setFieldsValue({
      name_en: breed.name_en,
      name_el: breed.name_el,
      animal_category_id: breed.animal_category_id,
      is_active: breed.is_active
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    const result = await adminBreedService.deleteBreed(id);
    if (result.success) {
      message.success(t('admin.breeds.deleteSuccess'));
      fetchBreeds(filterCategory);
    } else {
      message.error(result.error);
    }
  };

  const handleExport = () => {
    const exportColumns = [
      { title: 'ID', dataIndex: 'id' },
      { title: t('admin.breeds.nameEn'), dataIndex: 'name_en' },
      { title: t('admin.breeds.nameEl'), dataIndex: 'name_el' },
      { title: t('admin.breeds.animalCategory'), dataIndex: 'animal_category_name_en' },
      {
        title: t('common.status'),
        dataIndex: 'is_active',
        exportRender: (val) => val ? t('common.active') : t('common.inactive')
      }
    ];
    exportToExcel(breeds, exportColumns, 'breeds', t('admin.breeds.title'));
    message.success(t('admin.breeds.exportSuccess'));
  };

  const handleSubmit = async (values) => {
    const result = editingBreed
      ? await adminBreedService.updateBreed(editingBreed.id, values)
      : await adminBreedService.createBreed(values);

    if (result.success) {
      message.success(editingBreed ? t('admin.breeds.updateSuccess') : t('admin.breeds.createSuccess'));
      setModalVisible(false);
      form.resetFields();
      fetchBreeds(filterCategory);
    } else {
      message.error(result.error);
    }
  };

  const getCategoryName = (category) => {
    return i18n.language === 'el' ? (category.name_el || category.name_en) : category.name_en;
  };

  const columns = [
    {
      title: t('admin.breeds.animalCategory'),
      key: 'animal_category',
      render: (_, record) => (
        <Tag color="blue">
          {getAnimalNameWithEmoji(i18n.language === 'el' ? record.animal_category_name_el : record.animal_category_name_en)}
        </Tag>
      ),
      sorter: (a, b) => a.animal_category_name_en.localeCompare(b.animal_category_name_en)
    },
    {
      title: t('admin.breeds.nameEn'),
      dataIndex: 'name_en',
      key: 'name_en',
      sorter: (a, b) => a.name_en.localeCompare(b.name_en)
    },
    {
      title: t('admin.breeds.nameEl'),
      dataIndex: 'name_el',
      key: 'name_el'
    },
    {
      title: t('common.status'),
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? t('common.active') : t('common.inactive')}
        </Tag>
      ),
      filters: [
        { text: t('common.active'), value: true },
        { text: t('common.inactive'), value: false }
      ],
      onFilter: (value, record) => record.is_active === value
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            {t('common.edit')}
          </Button>
          <Popconfirm
            title={t('admin.breeds.deleteConfirm')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('common.yes')}
            cancelText={t('common.no')}
            okType="danger"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              {t('common.delete')}
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <BackButton to="/admin/dashboard" label={t('admin.backToDashboard')} />
      <Card
        title={t('admin.breeds.title')}
        extra={
          <Space>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExport}
              disabled={!breeds.length}
            >
              {t('admin.exportExcel')}
            </Button>
            <Button icon={<ReloadOutlined />} onClick={() => fetchBreeds(filterCategory)}>
              {t('common.refresh')}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              {t('admin.breeds.addBreed')}
            </Button>
          </Space>
        }
      >
        <p style={{ marginBottom: 16, color: '#666' }}>
          {t('admin.breeds.description')}
        </p>

        {/* Filter by animal category */}
        <Space style={{ marginBottom: 16 }}>
          <Select
            placeholder={t('admin.breeds.filterByCategory')}
            style={{ width: 200 }}
            allowClear
            value={filterCategory}
            onChange={handleFilterChange}
          >
            {animalCategories.map(cat => (
              <Option key={cat.id} value={cat.id}>
                {getAnimalNameWithEmoji(getCategoryName(cat))}
              </Option>
            ))}
          </Select>
        </Space>

        <Table
          columns={columns}
          dataSource={breeds}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            showTotal: (total) => t('admin.breeds.totalBreeds', { total })
          }}
        />
      </Card>

      <Modal
        title={editingBreed ? t('admin.breeds.editBreed') : t('admin.breeds.addBreed')}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ is_active: true }}
        >
          <Form.Item
            name="animal_category_id"
            label={t('admin.breeds.animalCategory')}
            rules={[{ required: true, message: t('admin.breeds.animalCategoryRequired') }]}
          >
            <Select placeholder={t('admin.breeds.selectCategory')}>
              {animalCategories.map(cat => (
                <Option key={cat.id} value={cat.id}>
                  {getAnimalNameWithEmoji(getCategoryName(cat))}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="name_en"
            label={t('admin.breeds.nameEn')}
            rules={[
              { required: true, message: t('admin.breeds.nameEnRequired') },
              { max: 100, message: t('admin.breeds.nameMaxLength') }
            ]}
          >
            <Input placeholder={t('admin.breeds.nameEnPlaceholder')} />
          </Form.Item>

          <Form.Item
            name="name_el"
            label={t('admin.breeds.nameEl')}
            rules={[
              { required: true, message: t('admin.breeds.nameElRequired') },
              { max: 100, message: t('admin.breeds.nameMaxLength') }
            ]}
          >
            <Input placeholder={t('admin.breeds.nameElPlaceholder')} />
          </Form.Item>

          <Form.Item
            name="is_active"
            label={t('common.status')}
            valuePropName="checked"
          >
            <Switch checkedChildren={t('common.active')} unCheckedChildren={t('common.inactive')} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingBreed ? t('common.update') : t('common.create')}
              </Button>
              <Button onClick={() => {
                setModalVisible(false);
                form.resetFields();
              }}>
                {t('common.cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BreedList;
